import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../config/apiConfig";

/**
 * Same download flow as My Orders: JWT + GET order/consumer-pdf/:orderId, blob save.
 */
export function useConsumerPdfDownload() {
  const [downloadingOrderId, setDownloadingOrderId] = useState(null);

  const downloadConsumerPdf = useCallback(async (order) => {
    const orderId = order?._id;
    if (!orderId) return false;

    const bookTitle = order.bookTitle || "Custom Coloring Book";
    const safeFileStem =
      bookTitle
        .replace(/[\/\\?%*:|"<>]/g, "")
        .replace(/\s+/g, " ")
        .trim() || "Coloring Book";
    const fallbackFilename = `${safeFileStem} | Memory Doodle Coloring Book.pdf`;

    setDownloadingOrderId(orderId);
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        toast.error("Please sign in again to download.");
        return false;
      }

      const url = `${API_BASE_URL}order/consumer-pdf/${orderId}`;
      const res = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        let message = "Could not download your digital copy. Please try again or contact support.";
        // 4xx messages are written for the customer ("Digital copy not available
        // yet"). A 5xx message is an internal stack-trace string — order #1747's
        // buyer was shown a raw Node header error — so never surface those.
        if (res.status < 500) {
          try {
            const errBody = await res.json();
            if (errBody?.message) message = errBody.message;
          } catch {
            /* ignore */
          }
        }
        toast.error(message);
        return false;
      }

      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition");
      let filename = fallbackFilename;
      if (cd) {
        // filename* carries the real title (curly apostrophes, accents);
        // filename= is the ASCII fallback the header also has to send.
        const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(cd);
        if (utf8?.[1]) {
          try {
            filename = decodeURIComponent(utf8[1].trim());
          } catch {
            /* fall through to the ASCII form */
          }
        }
        if (filename === fallbackFilename) {
          const m = /filename="([^"]+)"/.exec(cd) || /filename=([^;]+)/.exec(cd);
          if (m?.[1]) filename = m[1].trim();
        }
      }

      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      return true;
    } catch (err) {
      console.error("Consumer PDF download failed", err);
      toast.error("Download failed. Please try again.");
      return false;
    } finally {
      setDownloadingOrderId(null);
    }
  }, []);

  return { downloadConsumerPdf, downloadingOrderId };
}
